import { create } from 'zustand'
import api from '@/lib/api'
import type { PermissionMap } from '@/types'

// ── Types ──────────────────────────────────────────────────────────────────────

export interface UserProfile {
  id: string
  domain_id: string
  user_type_id: string
  parent_user_id: string | null
  name: string
  email: string | null
  phone: string | null
  timezone: string
  date_format: string
  is_active: boolean
  is_super: boolean
  is_admin: boolean
  last_login_at: string | null
  created_at: string
  updated_at: string
}

interface AuthState {
  user: UserProfile | null
  permissions: PermissionMap
  is_super: boolean
  /**
   * False until loadPermissions() has completed for the first time this session.
   * While false, usePermission() grants full access so the UI never appears locked
   * during the initial bootstrap fetch.
   */
  permissionsLoaded: boolean
  isAuthenticated: boolean

  /** Hydrate from localStorage on app mount — call before rendering routes. */
  init: () => void
  /** Call after a successful login to store tokens and mark authenticated. */
  setTokens: (access: string, refresh: string) => void
  /** Fetch and cache the current user profile (GET /users/me). */
  loadUser: () => Promise<void>
  /** Load permissions from cache (page refresh) or API (admin users). */
  loadPermissions: () => Promise<void>
  /** Full login flow: POST /auth/login → store tokens + permissions from response */
  login: (credentials: { login: string; password: string }) => Promise<void>
  /** POST /auth/logout, then clear all auth state. */
  logout: () => Promise<void>
  /** Clear auth state without an API call (used on session expiry). */
  clearAuth: () => void
}

// ── Helpers ────────────────────────────────────────────────────────────────────

/**
 * Converts a string[] of permission slugs (e.g. "vehicles:list", "drivers:create")
 * into a PermissionMap keyed by resource.
 */
function parseSlugsToMap(slugs: string[]): PermissionMap {
  const map: PermissionMap = {}
  for (const slug of slugs) {
    if (typeof slug !== 'string') continue
    const colonIdx = slug.indexOf(':')
    if (colonIdx === -1) continue
    const resource = slug.slice(0, colonIdx)
    const action = slug.slice(colonIdx + 1)
    if (!resource) continue

    if (!map[resource]) {
      map[resource] = { canRead: false, canCreate: false, canUpdate: false, canDelete: false }
    }
    // Any assigned slug grants canRead (controls sidebar visibility)
    map[resource].canRead = true
    if (action === 'create') {
      map[resource].canCreate = true
    } else if (action === 'update' || action === 'edit') {
      map[resource].canUpdate = true
    } else if (action === 'delete') {
      map[resource].canDelete = true
    } else if (action === 'manage') {
      map[resource].canCreate = true
      map[resource].canUpdate = true
      map[resource].canDelete = true
    }
  }
  return map
}

const PERM_CACHE_KEY = 'cached_permission_slugs'

// ── Store ──────────────────────────────────────────────────────────────────────

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  permissions: {},
  is_super: false,
  permissionsLoaded: false,
  isAuthenticated: false,

  init: () => {
    const token = localStorage.getItem('access_token')
    if (token) {
      set({ isAuthenticated: true })
    }

    // Listen for auth events dispatched by the Axios interceptor
    window.addEventListener('auth:session-expired', () => {
      get().clearAuth()
    })
    window.addEventListener('auth:token-refreshed', () => {
      // Token was silently refreshed — nothing extra to do here
    })
  },

  setTokens: (access, refresh) => {
    localStorage.setItem('access_token', access)
    localStorage.setItem('refresh_token', refresh)
    set({ isAuthenticated: true })
  },

  loadUser: async () => {
    const { data } = await api
      .get<{ success: true; data: UserProfile }>('/users/me')
      .then((r) => r.data)
    set({ user: data })
  },

  loadPermissions: async () => {
    const user = get().user

    const isSuperAdmin = get().is_super || (user?.is_super ?? false)
    if (isSuperAdmin) {
      set({ is_super: true, permissionsLoaded: true })
      return
    }

    if (user?.user_type_id) {
      try {
        const rawData = await api
          .get<{ success: true; data: unknown }>(`/user-types/${user.user_type_id}/permissions`)
          .then((r) => r.data.data)

        if (Array.isArray(rawData)) {
          if (rawData.length === 0) {
            set({ permissions: {}, permissionsLoaded: true })
            return
          }

          if (typeof rawData[0] === 'string') {
            // Legacy format: string[] of slugs
            const slugs = rawData as string[]
            localStorage.setItem(PERM_CACHE_KEY, JSON.stringify(slugs))
            set({ permissions: parseSlugsToMap(slugs), permissionsLoaded: true })
            return
          }

          // New format: PermissionSet[] with explicit CRUD flags
          type RawPS = { id: string; slug: string; can_read: boolean; can_create: boolean; can_update: boolean; can_delete: boolean }
          const sets = rawData as RawPS[]
          const map: PermissionMap = {}
          for (const ps of sets) {
            if (!ps.can_read && !ps.can_create && !ps.can_update && !ps.can_delete) continue
            const colonIdx = ps.slug.indexOf(':')
            const resource = colonIdx >= 0 ? ps.slug.slice(0, colonIdx) : ps.slug
            if (!resource) continue
            if (!map[resource]) map[resource] = { canRead: false, canCreate: false, canUpdate: false, canDelete: false }
            if (ps.can_read) map[resource].canRead = true
            if (ps.can_create) map[resource].canCreate = true
            if (ps.can_update) map[resource].canUpdate = true
            if (ps.can_delete) map[resource].canDelete = true
          }
          // Cache a synthetic slug list for page-refresh support
          const syntheticSlugs: string[] = []
          for (const [resource, flags] of Object.entries(map)) {
            if (flags.canRead) syntheticSlugs.push(`${resource}:list`)
            if (flags.canCreate) syntheticSlugs.push(`${resource}:create`)
            if (flags.canUpdate) syntheticSlugs.push(`${resource}:update`)
            if (flags.canDelete) syntheticSlugs.push(`${resource}:delete`)
          }
          localStorage.setItem(PERM_CACHE_KEY, JSON.stringify(syntheticSlugs))
          set({ permissions: map, permissionsLoaded: true })
          return
        }
      } catch {
        // 403 for regular users — fall through to cache
      }
    }

    // Fall back to slugs saved during the last login
    const cached = localStorage.getItem(PERM_CACHE_KEY)
    if (cached) {
      try {
        const slugs = JSON.parse(cached) as string[]
        set({ permissions: parseSlugsToMap(slugs), permissionsLoaded: true })
        return
      } catch {
        // Cache was corrupt
      }
    }

    set({ permissions: {}, permissionsLoaded: true })
  },

  login: async (credentials) => {
    interface LoginUser {
      is_super?: boolean
      permissions?: string[]
    }
    interface LoginResponse {
      success: true
      data: {
        access_token: string
        refresh_token: string
        expires_in: number
        user?: LoginUser
      }
    }

    const res = await api
      .post<LoginResponse>('/auth/login', credentials)
      .then((r) => r.data)

    get().setTokens(res.data.access_token, res.data.refresh_token)

    const loginUser = res.data.user

    // Super-admin bypass
    if (loginUser?.is_super) {
      set({ is_super: true })
    }

    // Cache permission slugs from login response so page refreshes work
    // (regular users cannot call /user-types/{id}/permissions directly)
    if (Array.isArray(loginUser?.permissions) && loginUser.permissions.length > 0) {
      const slugs = loginUser.permissions as string[]
      localStorage.setItem(PERM_CACHE_KEY, JSON.stringify(slugs))
      set({ permissions: parseSlugsToMap(slugs), permissionsLoaded: true })
    }

    // Load full user profile (needed for timezone, date_format, etc.)
    await get().loadUser()

    // If permissions weren't in the login response, fetch via the API
    if (!get().permissionsLoaded) {
      await get().loadPermissions()
    }
  },

  logout: async () => {
    const refreshToken = localStorage.getItem('refresh_token')
    try {
      if (refreshToken) {
        await api.post('/auth/logout', { refresh_token: refreshToken })
      }
    } catch {
      // Ignore logout API errors — proceed with local cleanup regardless
    } finally {
      get().clearAuth()
    }
  },

  clearAuth: () => {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('user_theme_primary')
    localStorage.removeItem(PERM_CACHE_KEY)
    set({ user: null, permissions: {}, is_super: false, permissionsLoaded: false, isAuthenticated: false })
  },
}))
